import { useState, type FormEvent } from 'react';
import { KeyRound, Loader2, ExternalLink } from 'lucide-react';
import { setApiKey, setApiUrl, getApiUrl, api } from '../lib/api';

interface Props {
  onAuth: () => void;
}

export function AuthGate({ onAuth }: Props) {
  const [key, setKey] = useState('');
  const [url, setUrl] = useState(getApiUrl() || 'https://factory.human-mcp.com');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!key.trim()) return;
    setLoading(true);
    setError('');

    setApiUrl(url);
    setApiKey(key.trim());

    try {
      await api.ping();
      onAuth();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connection failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex items-center justify-center min-h-screen p-4">
      <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-6">
        <div className="text-center space-y-2">
          <div className="text-4xl">🏭</div>
          <h1 className="text-xl font-semibold text-zinc-100">Agent Factory</h1>
          <p className="text-sm text-zinc-500">Connect to your control plane</p>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-zinc-400 mb-1.5">Factory URL</label>
            <input
              type="url"
              value={url}
              onChange={e => setUrl(e.target.value)}
              placeholder="https://factory.human-mcp.com"
              className="w-full px-3 py-2 bg-surface rounded-lg border border-border text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-accent transition-colors"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-zinc-400 mb-1.5">Admin API Key</label>
            <div className="relative">
              <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <input
                type="password"
                value={key}
                onChange={e => setKey(e.target.value)}
                placeholder="afk_..."
                autoFocus
                className="w-full pl-10 pr-3 py-2 bg-surface rounded-lg border border-border text-sm font-mono text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-accent transition-colors"
              />
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading || !key.trim()}
          className="w-full py-2.5 bg-accent hover:bg-accent-2 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          {loading ? 'Connecting...' : 'Connect'}
        </button>

        {error && (
          <p className="text-center text-sm text-red-400">{error}</p>
        )}

        <p className="text-center text-xs text-zinc-600">
          Key at <code className="text-zinc-500">~/.pi/agent-factory/admin.key</code>
        </p>
      </form>
    </div>
  );
}
